import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { User } from 'src/app/models/users.model';
import { CountryCode } from 'src/app/models/country.enum';
import { environment } from 'src/environments/environment';

const firestore = firebase.firestore;

interface CreateUserRequest {
  email: string;
  name: string;
  countryCode: CountryCode;
  mobile: string;
  companyName: string;
  agreedToTerms: boolean;
  password: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly apiUrl = `${environment.functionsRootUrl}/userManagement-userManagement`;

  constructor(
    private http: HttpClient,
    private auth: AngularFireAuth,
  ) {}

  async createUser(request: CreateUserRequest): Promise<User> {
    try {
      const response = await this.http.post<{ user: User }>(`${this.apiUrl}/create`, request).toPromise();

      // Sign in the newly created user
      await this.auth.signInWithEmailAndPassword(request.email, request.password);

      return response!.user;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async signIn(email: string, password: string): Promise<void> {
    try {
      await this.auth.signInWithEmailAndPassword(email, password);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateProfile(uid: string, updates: Partial<User>): Promise<void> {
    try {
      await firestore()
        .doc(`users/${uid}`)
        .update({
          ...updates,
          updatedAt: firestore.FieldValue.serverTimestamp(),
        });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    // Our functions return `{ error }` bodies; Firebase client SDK errors carry `message`
    const message = error?.error?.error || error?.error?.message || error?.message || '';

    if (message.includes('email-already-in-use')) {
      return new Error('An account with this email already exists. Please sign in instead.');
    }
    if (message.includes('wrong-password') || message.includes('user-not-found')) {
      return new Error('Invalid email or password. Please try again.');
    }
    if (message.includes('weak-password')) {
      return new Error('Password should be at least 6 characters long.');
    }
    if (message.includes('invalid-email')) {
      return new Error('Please enter a valid email address.');
    }
    if (message.includes('too-many-requests')) {
      return new Error('Too many failed attempts. Please try again later.');
    }

    return new Error(message || 'Something went wrong. Please try again.');
  }
}
